import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Instagram, Facebook, Linkedin } from 'lucide-react';
import { postContact, resolveImg } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useSettings } from '../lib/SettingsContext';

import { useLang } from '../lib/i18n';
export default function Contacto() {
  const { ui } = useLang();
  const { settings } = useSettings();
  const { toast } = useToast();
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', mensaje: '', newsletter: true });
  const [sending, setSending] = useState(false);
  const heroImg = resolveImg(settings.hero_images?.[2] || settings.hero_images?.[0] || '');

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postContact(form);
      toast({ title: 'Mensaje enviado', description: 'Gracias por escribirnos. Te responderemos lo antes posible.' });
      setForm({ nombre: '', email: '', telefono: '', mensaje: '', newsletter: true });
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo enviar. Inténtalo de nuevo.', variant: 'destructive' });
    }
    setSending(false);
  };

  return (
    <div>
      <section className="relative h-[55vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 gd-hero-bg" style={{ backgroundImage: `url(${heroImg})` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,13,14,0.6), rgba(13,13,14,0.95))' }} />
        <div className="relative z-10 text-center px-6">
          <div className="gd-eyebrow mb-3">{ui('contact.eyebrow')}</div>
          <h1 className="font-script text-[#c9a063] text-5xl md:text-7xl leading-none">{ui('contact.title')}</h1>
          <p className="font-serif-display text-white text-2xl md:text-3xl mt-4">{ui('contact.sub')}</p>
        </div>
      </section>

      <section className="py-20 px-6 md:px-10 bg-[#0d0d0e]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="gd-eyebrow mb-3">{ui('contact.reach_us')}</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-white leading-tight">¿Preparado para tu próxima experiencia?</h2>
              <p className="text-white/65 mt-5 text-lg leading-relaxed">Cuéntanos qué buscas y diseñaremos contigo el plan perfecto. También puedes suscribirte a nuestra newsletter para no perderte ningún torneo.</p>
            </div>
            <div className="space-y-4">
              <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-4 group">
                <div className="w-11 h-11 border border-[#c9a063]/40 flex items-center justify-center text-[#c9a063]"><Phone size={16} /></div>
                <div>
                  <div className="text-[10px] tracking-widest text-white/50 uppercase">{ui('form.phone')}</div>
                  <div className="text-white group-hover:text-[#c9a063] transition-colors">{settings.contact_phone}</div>
                </div>
              </a>
              <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-4 group">
                <div className="w-11 h-11 border border-[#c9a063]/40 flex items-center justify-center text-[#c9a063]"><Mail size={16} /></div>
                <div>
                  <div className="text-[10px] tracking-widest text-white/50 uppercase">{ui('form.email')}</div>
                  <div className="text-white group-hover:text-[#c9a063] transition-colors">{settings.contact_email}</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 border border-[#c9a063]/40 flex items-center justify-center text-[#c9a063]"><MapPin size={16} /></div>
                <div>
                  <div className="text-[10px] tracking-widest text-white/50 uppercase">{ui('form.location')}</div>
                  <div className="text-white">{settings.contact_address}</div>
                </div>
              </div>
            </div>
            <div>
              <div className="gd-eyebrow mb-4">{ui('nav.follow_us')}</div>
              <div className="flex items-center gap-3">
                {settings.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noreferrer" className="w-11 h-11 border border-white/15 flex items-center justify-center text-white/70 hover:border-[#c9a063] hover:text-[#c9a063] transition-colors"><Instagram size={16} /></a>}
                {settings.social_facebook && <a href={settings.social_facebook} target="_blank" rel="noreferrer" className="w-11 h-11 border border-white/15 flex items-center justify-center text-white/70 hover:border-[#c9a063] hover:text-[#c9a063] transition-colors"><Facebook size={16} /></a>}
                {settings.social_linkedin && <a href={settings.social_linkedin} target="_blank" rel="noreferrer" className="w-11 h-11 border border-white/15 flex items-center justify-center text-white/70 hover:border-[#c9a063] hover:text-[#c9a063] transition-colors"><Linkedin size={16} /></a>}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form onSubmit={submit} className="gd-card p-8 md:p-10 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] tracking-widest text-white/50 uppercase block mb-2">Nombre *</label>
                  <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="w-full bg-transparent border border-white/15 text-white px-3 py-3 text-sm focus:border-[#c9a063] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] tracking-widest text-white/50 uppercase block mb-2">Email *</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border border-white/15 text-white px-3 py-3 text-sm focus:border-[#c9a063] focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-widest text-white/50 uppercase block mb-2">{ui('form.phone')}</label>
                <input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="w-full bg-transparent border border-white/15 text-white px-3 py-3 text-sm focus:border-[#c9a063] focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] tracking-widest text-white/50 uppercase block mb-2">Mensaje *</label>
                <textarea required rows={5} value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })} className="w-full bg-transparent border border-white/15 text-white px-3 py-3 text-sm focus:border-[#c9a063] focus:outline-none resize-none" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.newsletter} onChange={(e) => setForm({ ...form, newsletter: e.target.checked })} className="mt-1 accent-[#c9a063]" />
                <span className="text-white/65 text-sm">Quiero suscribirme a la newsletter de Golf Divino para recibir novedades y experiencias.</span>
              </label>
              <p className="text-white/40 text-xs">Al enviar este formulario aceptas nuestra política de privacidad.</p>
              <button disabled={sending} type="submit" className="gd-btn-gold px-8 py-3.5 text-[11px] uppercase tracking-[0.3em] font-sans-ui font-medium inline-flex items-center gap-2 disabled:opacity-60">{sending ? ui('form.sending') : <>{ui('btn.send_msg')}  <Send size={13} /></>}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
